import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { formatPhoneForDisplay } from '../../utils/phoneUtils';

const maskValue = (value, type) => {
  if (!value) return '';
  const str = String(value);
  switch (type) {
    case 'email': {
      if (!str.includes('@')) return '•••';
      const [local, domain] = str.split('@');
      const visible = local.length <= 3 ? local.charAt(0) : local.substring(0, 3);
      return `${visible}•••@${domain}`;
    }
    case 'phone':
      return str.length > 4 ? `••••${str.slice(-4)}` : '•••';
    case 'nric':
      return str.length > 5
        ? `${str.charAt(0)}${'•'.repeat(str.length - 5)}${str.slice(-4)}`
        : '•••';
    case 'passport':
    case 'wp_number':
    case 'account':
      return str.length > 4 ? `••••${str.slice(-4)}` : '•••';
    case 'currency':
      return 'SGD ••••.••';
    case 'date':
      return '••••••••••';
    case 'address':
      return 'Address hidden for privacy';
    case 'name':
      return str
        .split(' ')
        .map((p) => p.charAt(0) + '•'.repeat(Math.max(0, p.length - 1)))
        .join(' ');
    case 'medical':
      return 'Medical info on file';
    default:
      return '•••';
  }
};

const formatVisible = (value, type) => {
  if (type === 'date') return formatDate(value);
  if (type === 'phone') return formatPhoneForDisplay(value);
  if (type === 'currency') {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
    }).format(value);
  }
  return value;
};

const THEME_BUTTON = {
  admin: 'icon-button-admin',
  employee: 'icon-button',
};

/**
 * Full-round mask/reveal control for sensitive values.
 *
 * Uses DS micro tier (`icon-button-*` + `icon-button-sm`) so the hover well
 * is a true circle inset inside `.field-display` pills — never `rounded-md`.
 *
 * @param {boolean}  isVisible
 * @param {function} onClick
 * @param {'admin'|'employee'} [theme='admin']
 * @param {'field'|'inline'} [placement='field'] - field = absolute in pill; inline = next to text
 * @param {boolean}  [multiline=false]
 * @param {string}   [positionClass] - Override default placement classes when needed
 * @param {string}   [titleShow]
 * @param {string}   [titleHide]
 * @param {string}   [className]
 */
export const SensitiveRevealButton = ({
  isVisible,
  onClick,
  theme = 'admin',
  placement = 'field',
  multiline = false,
  positionClass: positionClassOverride,
  titleShow = 'Show sensitive data',
  titleHide = 'Hide sensitive data',
  className = '',
}) => {
  const title = isVisible ? titleHide : titleShow;
  const themeClass = THEME_BUTTON[theme] || THEME_BUTTON.admin;
  const defaultPositionClass =
    placement === 'inline'
      ? 'flex-shrink-0'
      : multiline
        ? 'absolute right-2 top-2'
        : 'absolute right-2 top-1/2 -translate-y-1/2';
  const positionClass = positionClassOverride ?? defaultPositionClass;
  const revealedClass = isVisible
    ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${themeClass} icon-button-sm ${positionClass} ${revealedClass} ${className}`.trim()}
      title={title}
      aria-label={title}
      aria-pressed={isVisible}
    >
      {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );
};

/**
 * Labeled field display with built-in mask/reveal toggle.
 *
 * Renders a `.field-display` pill with a `.field-label` above it.
 * Manages its own per-field visibility state internally.
 *
 * @param {string}  label      - Field label (omit or "" to hide)
 * @param {string}  value      - Raw value (null/undefined renders "Not provided")
 * @param {string}  fieldName  - Unique key for visibility toggling
 * @param {string}  [type]     - Masking type: nric|email|phone|passport|wp_number|
 *                               account|currency|date|address|name|medical|default
 * @param {boolean} [multiline] - Use rounded-2xl + taller min-height for addresses
 * @param {boolean} [mono]     - Monospace font (NRIC, account numbers)
 * @param {ReactNode} [icon]   - Optional leading icon
 * @param {'admin'|'employee'} [theme='admin']
 */
export const SensitiveFieldDisplay = ({
  label,
  value,
  fieldName,
  type = 'default',
  multiline = false,
  mono = false,
  icon,
  theme = 'admin',
  className = '',
}) => {
  const [visible, setVisible] = useState(false);

  const display = value
    ? visible
      ? formatVisible(value, type)
      : maskValue(value, type)
    : 'Not provided';

  return (
    <div className={className}>
      {label && <label className="field-label">{label}</label>}
      <div className="relative">
        <p
          className={`field-display ${multiline ? 'field-display-multiline' : ''} ${
            mono ? 'font-mono' : ''
          } ${value ? 'field-display-sensitive' : ''}`}
        >
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className={`break-words whitespace-normal ${multiline ? 'block' : ''}`}>{display}</span>
        </p>
        {value && (
          <SensitiveRevealButton
            isVisible={visible}
            onClick={() => setVisible((v) => !v)}
            theme={theme}
            multiline={multiline}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Inline mask/reveal toggle for use within table rows or text blocks.
 *
 * Unlike SensitiveFieldDisplay, this renders inline (no label, no pill container).
 *
 * @param {string}    value     - Raw value
 * @param {string}    fieldName - Unique key for visibility toggling
 * @param {string}    [type]    - Masking type (same options as SensitiveFieldDisplay)
 * @param {'admin'|'employee'} [theme='admin']
 * @param {ReactNode} [children] - Custom render; if omitted, renders masked/unmasked text
 */
export const SensitiveInlineToggle = ({
  value,
  fieldName,
  type = 'default',
  theme = 'admin',
  children,
  className = '',
}) => {
  const [visible, setVisible] = useState(false);

  const display = value
    ? visible
      ? formatVisible(value, type)
      : maskValue(value, type)
    : '';

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {children || <span>{display}</span>}
      {value && (
        <SensitiveRevealButton
          isVisible={visible}
          onClick={() => setVisible((v) => !v)}
          theme={theme}
          placement="inline"
        />
      )}
    </span>
  );
};

export { maskValue, formatVisible };
export default SensitiveFieldDisplay;
